import { Injectable } from '@angular/core';
import * as dayjs from 'dayjs';
import { defineLocale } from 'ngx-bootstrap/chronos';
import { BsDatepickerConfig } from 'ngx-bootstrap/datepicker';
import { enGbLocale } from 'ngx-bootstrap/locale';
import { TimepickerConfig } from 'ngx-bootstrap/timepicker';
import { arrayMax, Dict, includes } from './collection.utils';
import { adjustDateToUTC, isDateString } from './date.utils';

export const NO_ACCESS_ROUTE = '/403';

// eslint-disable-next-line
declare function require(name: string): any;
// eslint-disable-next-line
const bigInt = require('big-integer');

function isObjectLike(val: unknown): boolean {
  if (val === null) {
    return false;
  }
  return typeof val === 'function' || typeof val === 'object';
}

/**
 * Walks an API response and converts in-place:
 *  - ISO date strings → `Date` instances
 *  - `*_flag` properties → booleans
 *
 * Date parsing uses `dayjs(...).toDate()` rather than `new Date(...)`. Native
 * `Date` parsing is unsafe for two of our common API shapes:
 *  - `"YYYY-MM-DD"` is always parsed as UTC midnight by `new Date()`, which
 *    shifts the displayed day by one in any timezone west of UTC.
 *  - `"YYYY-MM-DDTHH:mm:ss"` (no offset) is parsed as local on V8 but as UTC
 *    on Safari, so the same payload renders differently per browser.
 * Dayjs parses both as local time consistently across browsers, while still
 * honoring `Z` / `±HH:MM` offsets when present. This keeps date-only fields
 * (e.g. `birth_date`, `registration_date`) showing the same calendar day for
 * every user regardless of timezone or browser.
 */
export function deserialize<T>(obj: unknown): T {
  if (obj && typeof obj === 'object') {
    for (const key of Object.getOwnPropertyNames(obj)) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-explicit-any
      const childObj: unknown = (obj as any)[key];
      if (isObjectLike(childObj)) {
        deserialize(childObj);
      } else if (isDateString(childObj)) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-explicit-any
        (obj as any)[key] = dayjs(childObj).toDate();
      } else if (key.endsWith('_flag')) {
        // TODO: Check value of childObj and give a warning or error if it isn't int2.
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-explicit-any
        (obj as any)[key] = !!childObj;
      }
    }
  }
  return obj as T;
}

/**
 * Serializes an payloadf object by processing its properties. If a property value is a Date object,
 * it adjusts the date to UTC format; otherwise, it retains the original value.
 */
export function serialize<T extends Partial<{ [key: string]: unknown }>>(obj: T): { [key: string]: unknown } {
  if (obj && typeof obj === 'object') {
    const payload = Object.keys(obj).reduce((res: { [key: string]: unknown }, key) => {
      const val = obj[key];
      if (val instanceof Date) {
        res[key] = adjustDateToUTC(val);
      } else {
        res[key] = obj[key];
      }
      return res;
    }, {});
    return payload;
  }
  return obj;
}

export function flatten1<T>(arr: T[][]): T[] {
  return arr.reduce((flat, toFlatten) => flat.concat(toFlatten), []);
}

export function flatten2<T>(arr: T[][][]): T[] {
  const output: T[] = [];
  for (const outer of arr) {
    output.concat(flatten1(outer));
  }
  return output;
}

export function formatTimeByDate(dt: Date, defaultTime: string): string {
  if (dt) {
    return dayjs(dt).format('hh:mm:ss');
  }
  return defaultTime;
}

@Injectable()
export class CustomBsDatepickerConfig extends BsDatepickerConfig {
  public override containerClass = 'theme-blue';
  public override dateInputFormat = 'YYYY-MM-DD';
  public override rangeInputFormat = 'YYYY-MM-DD';
  public startingDay = 1;
  public locale = enGbLocale.abbr;

  constructor() {
    super();
    if (typeof enGbLocale.abbr === 'string') {
      defineLocale(enGbLocale.abbr, enGbLocale);
    }
  }
}

@Injectable()
export class CustomTimepickerConfig extends TimepickerConfig {
  public override hourStep = 2;
  public override minuteStep = 10;
  public override showMeridian = false;
  public override readonlyInput = false;
  public override mousewheel = true;
}

export const highchartsColors = [
  '#255092',
  '#FFD22B',
  '#EA7000',
  '#0bb778',
  '#229DAE',
  '#4D85D9',
  '#f86c6b',
  '#600FA2',
  '#a8352f',
];

// This should reflect the colors in _bootstrap_variables.scss
// TODO: Let's hope we can remove this with highcharts.
export const COLOR = {
  'dark-blue': '#255092',
  blue: '#4d85d9',
  indigo: '#6f42c1',
  cyan: '#00acc1',
  green: '#27AE60',
  'light-green': '#60db94',
  yellow: '#FFD22B',
  orange: '#EA7000',
  red: '#a8352f',
  gray: '#828282',
  white: '#fff',
  disabeld: '#D3D3D3',
};

// Mapped to risk field
export const RISK_COLOR = {
  very_low: COLOR['light-green'],
  low: COLOR.green,
  medium: COLOR.yellow,
  high: COLOR.orange,
  very_high: COLOR.red,
  unset: COLOR.white,
} as const;

export type RiskGroupKey = keyof typeof RISK_COLOR;
// TODO: move to company-block
export function isRiskGroupKey(item: string): item is RiskGroupKey {
  return !!item && Object.keys(RISK_COLOR).includes(item);
}

// type MeasureValueDefault = keyof Pick<
//   WatchlistCompanyRiskScoreMeasureAggregateEntity,
//   | 'measure_value_default_very_low_count'
//   | 'measure_value_default_low_count'
//   | 'measure_value_default_medium_count'
//   | 'measure_value_default_high_count'
//   | 'measure_value_default_very_high_count'
//   | 'measure_value_default_unset_count'
// >;

export const RISK_GROUP_KEY_MAP: Record<RiskGroupKey, string> = {
  very_low: 'measure_value_default_very_low_count',
  low: 'measure_value_default_low_count',
  medium: 'measure_value_default_medium_count',
  high: 'measure_value_default_high_count',
  very_high: 'measure_value_default_very_high_count',
  unset: 'measure_value_default_unset_count',
};

export const RISK_COLOR_CLASS: Record<RiskGroupKey, string> = {
  very_low: 'light-green',
  low: 'success',
  medium: 'warning',
  high: 'danger',
  very_high: 'dark-red',
  unset: 'dark',
};

/**
 * Highlights occurrences of `queryElms` within `content` by wrapping them in a span.
 *
 * The result is intended for `[innerHTML]`, so the untrusted `content` is HTML-escaped FIRST and
 * only the trusted highlight spans are added on top. This is safe by default, callers do not need
 * to escape beforehand. See {@link wrapHighlightSpans}.
 *
 * Newlines are converted to `<br>` so line breaks in the source text (including the snippet
 * separators produced by `skipGram`) render correctly without needing `white-space: pre-line` on
 * every container.
 */
export function highlightText(content: string, queryElms: string[], highlightClass = 'weight-bolder'): string {
  if (!content) {
    return content;
  }
  // Escape, then highlight (so highlight terms can't match the markup we add), then turn newlines
  // into <br> last (so the <br> we insert can't be split by a highlight span).
  return wrapHighlightSpans(escapeHtml(content), queryElms, highlightClass).replace(/\r\n|\r|\n/g, '<br>');
}

/**
 * Wraps occurrences of `terms` in already-HTML-safe `safeText` with a highlight span.
 *
 * `safeText` MUST already be HTML-escaped (or otherwise trusted) — this function only adds markup,
 * it does not escape the surrounding text. The search terms are escaped to match the escaped text.
 */
export function wrapHighlightSpans(safeText: string, terms: string[] | undefined, highlightClass = 'weight-bolder'): string {
  const escapedTerms = (terms ?? []).filter((term) => !!term).map((term) => escapeRegExp(escapeHtml(term)));
  if (!escapedTerms.length) {
    return safeText;
  }
  return safeText.replace(new RegExp(escapedTerms.join('|'), 'gi'), (match) => {
    return '<span class="' + highlightClass + '">' + match + '</span>';
  });
}

export function escapeRegExp(text: string): string {
  // https://stackoverflow.com/questions/3446170/escape-string-for-use-in-javascript-regex
  return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export function escapeHtml(text: string): string {
  // Escape characters that are significant in HTML so untrusted text can be safely placed
  // into markup that is rendered via [innerHTML].
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export class NotImplemented extends Error {
  constructor(message?: string) {
    if (message) {
      message = 'Not implemented yet: ' + message;
      super(message);
    } else {
      super('Not implemented yet');
    }
  }
}

export function getRootHostname(): string {
  const hostParts = window.location.hostname.split('.');
  return hostParts.slice(hostParts.length - 2).join('.');
}

export function getStrippedHostname(numberOfSubdomainsToStrip = 1): string {
  const hostParts = window.location.hostname.split('.');
  return hostParts.slice(numberOfSubdomainsToStrip).join('.');
}

export function uuid(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
    // eslint-disable-next-line no-bitwise
    const random = (Math.random() * 16) | 0; // Nachkommastellen abschneiden
    const value = char === 'x' ? random : (random % 4) + 8; // Bei x Random 0-15 (0-F), bei y Random 0-3 + 8 = 8-11 (8-b) gemäss RFC 4122
    return value.toString(16); // Hexadezimales Zeichen zurückgeben
  });
}

export function translationLookup(text: string, lookupDict: Dict<string>, sep = '.'): string {
  let textOutput: string;
  if (includes(text, sep)) {
    const [entity, field] = text.split(sep);
    textOutput =
      (lookupDict[entity] ? lookupDict[entity] : entity) + '.' + (lookupDict[field] ? lookupDict[field] : field);
  } else {
    textOutput = lookupDict[text] ? lookupDict[text] : text;
  }
  return textOutput;
}

export function exponentialSlider(actualSlider: number, maxSlider: number, maxValue: number): number {
  let actualValue: number;
  if (actualSlider < 1) {
    actualValue = 0;
  } else {
    const n = Math.pow(maxValue, 1.0 / maxSlider);
    actualValue = Math.pow(n, actualSlider);
  }
  return actualValue;
}

export function inverseExponentialSlider(actualValue: number, maxSlider: number, maxValue: number): number {
  let actualSlider: number;
  if (actualValue === 0) {
    actualSlider = 0;
  } else {
    const n = Math.pow(maxValue, 1.0 / maxSlider);
    actualSlider = Math.log(actualValue) / Math.log(n);
  }
  return actualSlider;
}

export function normalCase(word: string): string {
  word = word.toLowerCase();

  return word.replace(/^./, word[0].toUpperCase());
}

export function getVisibilityChangeEventName(): string | null {
  if (typeof document['visibilityState'] !== 'undefined') {
    return 'visibilitychange';
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-explicit-any
  } else if (typeof (document as any)['mozHidden'] !== 'undefined') {
    return 'mozvisibilitychange';
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-explicit-any
  } else if (typeof (document as any)['msHidden'] !== 'undefined') {
    return 'msvisibilitychange';
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-explicit-any
  } else if (typeof (document as any)['webkitHidden'] !== 'undefined') {
    return 'webkitvisibilitychange';
  } else {
    return null;
  }
}

export function getVisibilityStateName(): string | null {
  if (typeof document['visibilityState'] !== 'undefined') {
    return 'hidden';
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-explicit-any
  } else if (typeof (document as any)['mozHidden'] !== 'undefined') {
    return 'mozVisibilityState';
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-explicit-any
  } else if (typeof (document as any)['msHidden'] !== 'undefined') {
    return 'msVisibilityState';
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-explicit-any
  } else if (typeof (document as any)['webkitHidden'] !== 'undefined') {
    return 'webkitVisibilityState';
  } else {
    return null;
  }
}

export function toTitleCase(str: string): string {
  if (str) {
    return str
      .split(' ')
      .filter((i) => i && i.length)
      .map((w) => w[0].toUpperCase() + w.substr(1).toLowerCase())
      .join(' ');
  } else {
    return str;
  }
}

export function getYearsSince(firstYear: number): number[] {
  const years: number[] = [];
  const thisYear = new Date().getFullYear() - 1;
  for (let year = thisYear; year >= firstYear; year--) {
    years.push(year);
  }
  return years;
}

export function getYearsFromYear(numberOfYears: number, startYear = 0): number[] {
  const years: number[] = [];
  const minimumYear = startYear - numberOfYears;
  for (let year = startYear; year > minimumYear; year--) {
    years.push(year);
  }
  return years;
}

export function getYearsFromToday(numberOfYears: number, offsetYearNegative = 0): number[] {
  const years: number[] = [];
  const thisYear = new Date().getFullYear() - offsetYearNegative;
  const minimumYear = thisYear - numberOfYears;
  for (let year = thisYear; year > minimumYear; year--) {
    years.push(year);
  }
  return years;
}

// https://stackoverflow.com/a/8273091/604048
export function range(start: number, stop?: number, step = 1): number[] {
  if (stop == null) {
    stop = start;
    start = 0;
  }
  const result: number[] = [];
  if ((step > 0 && start >= stop) || (step < 0 && start <= stop)) {
    return result;
  }
  for (let i = start; step > 0 ? i < stop : i > stop; i += step) {
    result.push(i);
  }
  return result;
}

export enum TrimSide {
  Left = 'left',
  Right = 'right',
  Both = 'both',
}

export function trimChar(str: string, charToRemove: string, trimSide: TrimSide = TrimSide.Both): string {
  // https://stackoverflow.com/a/26156806/604048

  if (trimSide === TrimSide.Left || trimSide === TrimSide.Both) {
    while (str.charAt(0) === charToRemove) {
      str = str.substring(1);
    }
  }

  if (trimSide === TrimSide.Right || trimSide === TrimSide.Both) {
    while (str.charAt(str.length - 1) === charToRemove) {
      str = str.substring(0, str.length - 1);
    }
  }

  return str;
}

export function trimCharLeft(str: string, charToRemove: string): string {
  return trimChar(str, charToRemove, TrimSide.Left);
}

export function trimCharRight(str: string, charToRemove: string): string {
  return trimChar(str, charToRemove, TrimSide.Right);
}

const BASE62_ALPHABET = '0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ';
const BASE64_ALPHABET = '0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ+/';

// https://gist.github.com/mqchen/552e679d4adf9d37ed58

export function encodeBinaryArrayToBaseN(binaryArray: number[], alphabet: string = BASE62_ALPHABET): string {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
  return bigInt
    .fromArray(binaryArray, 2)
    .toArray(alphabet.length)
    .value.map((idx: number) => alphabet[idx])
    .join('');
}

export function encodeIdsToBaseNMask(ids: number[], alphabet: string = BASE62_ALPHABET): string {
  const maxId = arrayMax(ids, (n) => n);
  if (maxId === null) {
    return '';
  }
  const binaryMask = range(maxId + 1)
    .map((id) => (ids.indexOf(id) >= 0 ? 1 : 0))
    .reverse();
  return encodeBinaryArrayToBaseN(binaryMask, alphabet);
}

export function decodeBaseNToBinaryArray(baseNMask: string, alphabet: string = BASE62_ALPHABET): number[] {
  const value = baseNMask.split('').map((c) => alphabet.indexOf(c));
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
  return bigInt.fromArray(value, alphabet.length).toArray(2).value;
}

export function decodeBaseNMaskToIds(baseNMask: string, alphabet: string = BASE62_ALPHABET): number[] {
  const binaryMask = decodeBaseNToBinaryArray(baseNMask, alphabet);
  let id = 0;
  const ids: number[] = [];
  for (const bin of binaryMask.reverse()) {
    if (bin > 0) {
      ids.push(id);
    }
    id++;
  }
  return ids;
}

// Removes spaces and adds prefix
export function postgreSQLIertStatementForTranslations(
  translateKeys: string[],
  prefix: string,
  uppercaseFirstLetter = true,
): string {
  /* Example:
  prefix = 'company_event_types.'
  const translateKeys = [
    'employee_dismissal_business_conditions',
    'patent_dispute',
    'debt_negotiation_process',
    'contract_rescission',
    'monetary_claim',
    'compulsory_acquisition',
    'contract_breach',
  ]; */
  const translated = [];
  for (const key of translateKeys) {
    let sentence = key;
    if (uppercaseFirstLetter) {
      sentence = sentence.charAt(0).toUpperCase() + sentence.slice(1);
    }
    translated.push({ translation: sentence.split('_').join(' '), key: prefix + key });
  }
  const insertStatement = [];
  for (const trans of translated) {
    insertStatement.push(
      'INSERT INTO "app"."app_translation"("language_code", "app_translation_key", "translation") VALUES (\'nb\', \'' +
        trans.key +
        "', '" +
        trans.translation +
        "');",
    );
    insertStatement.push(
      'INSERT INTO "app"."app_translation"("language_code", "app_translation_key", "translation") VALUES (\'en\', \'' +
        trans.key +
        "', '" +
        trans.translation +
        "');",
    );
  }
  return insertStatement.join('   ');
}

export function scrollToElement(elementId: string, headerOffset = 85): void {
  const element = document.getElementById(elementId);
  if (!element) {
    return;
  }
  const bodyRect = document.body.getBoundingClientRect().top;
  const elementRect = element.getBoundingClientRect().top;
  const elementPosition = elementRect - bodyRect;
  const offsetPosition = elementPosition - headerOffset;
  window.scrollTo({
    top: offsetPosition,
    behavior: 'smooth',
  });
}

const ORGANIZATION_TYPE_ABBREVIATIONS = ['AS', 'A/S', 'A\\S', 'ASA', 'SA', 'ENK', 'S.A.', 'ANS'];

export function simplifyCompanyName(
  companyName: string,
  organizationTypeAbbreviations = ORGANIZATION_TYPE_ABBREVIATIONS,
): string {
  for (let abbreviation of organizationTypeAbbreviations) {
    abbreviation = abbreviation.toLowerCase();
    if (companyName.length > 4 && companyName.toLowerCase().endsWith(' ' + abbreviation)) {
      companyName = companyName.slice(0, -abbreviation.length - 1);
    }
    if (companyName.length > 4 && companyName.toLowerCase().startsWith(abbreviation + ' ')) {
      companyName = companyName.slice(abbreviation.length + 1);
    }
  }
  return companyName;
}

const STOP_WORDS = ['&', 'and', 'og', 'i'];

export function removeCompanyNameAbbreviationsAndStopWords(
  companyName: string,
  filterWords = [...ORGANIZATION_TYPE_ABBREVIATIONS, ...STOP_WORDS],
): string {
  let words = companyName.trim().split(/\s+/);
  const uppercaseFilterWords = filterWords.map((a) => a.toUpperCase());
  words = words.filter((word) => {
    return !includes(uppercaseFilterWords, word.toUpperCase());
  });
  return words.join(' ');
}

export function titleCaseCompanyName(
  companyName: string | null,
  organizationTypeAbbreviations = ORGANIZATION_TYPE_ABBREVIATIONS,
): string | null {
  if (typeof companyName !== 'string' || companyName.length === 0) {
    return companyName;
  }

  let words = companyName.trim().split(/\s+/);

  words = words.map((word) => {
    for (const abbreviation of organizationTypeAbbreviations) {
      if (word.toUpperCase() === abbreviation.toUpperCase()) {
        // Return the abbreviation unchanged
        return abbreviation;
      }
    }
    // Return the word title-cased
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
  });

  return words.join(' ');
}
